import { on } from "svelte/events";
import type { z, ZodType } from "zod/v4";
import type { Diagnostics } from "@libs/diagnostics/Diagnostics.js";

export interface LocalStorageValue<Schema> {
	schema: Schema;
	value: z.infer<Schema>;
	loaded: boolean;
}

export function localStorageState<Schema extends ZodType>(
	schema: Schema,
	localStorageKey: string,
	initialValue: z.infer<Schema>,
	diagnostics: Diagnostics,
): LocalStorageValue<Schema> {
	type Value = z.infer<Schema>;

	let value = $state(structuredClone(initialValue));

	if (typeof localStorage === "undefined") {
		return {
			get loaded(): boolean {
				return false;
			},
			get value(): Value {
				return value;
			},
			schema,
			set value(newValue: Value) {
				value = newValue;
			},
		};
	}

	function saveValueToLocalStorage(value: Value): void {
		if (value === null || value === undefined) {
			localStorage.removeItem(localStorageKey);
		} else {
			localStorage.setItem(localStorageKey, JSON.stringify(value));
		}
	}

	function getParsedValue(): Value | null {
		const storedValue = localStorage.getItem(localStorageKey);
		if (storedValue !== null) {
			let json: unknown;
			try {
				json = JSON.parse(storedValue);
			} catch (error) {
				diagnostics.error(
					"Failed to parse stored JSON value from localStorage, resetting to initial value",
					String(error),
				);
				saveValueToLocalStorage(initialValue);
				return getParsedValue();
			}

			const parsed = schema.safeParse(json);
			if (parsed.success) {
				return parsed.data;
			} else {
				diagnostics.error(
					"Stored value in localStorage does not adhere to the schema, resetting to initial value",
					parsed.error,
				);
				saveValueToLocalStorage(initialValue);
				return getParsedValue();
			}
		}
		return null;
	}

	const parsed = getParsedValue();
	if (parsed !== null) {
		value = parsed;
	}

	on(window, "storage", () => {
		const parsed = getParsedValue();
		if (parsed !== null) {
			value = parsed;
		} else {
			value = structuredClone(initialValue);
		}
	});

	return {
		get loaded(): boolean {
			return true;
		},
		get value(): Value {
			return value;
		},
		schema,
		set value(newValue: Value) {
			saveValueToLocalStorage(newValue);
			value = newValue;
		},
	};
}
